import { CODE_ANALYSIS_VALUE_PREFIX } from "../Constants";
import { EvaluateExpressionType, ImportModule, Pane, PaneType } from "../Types";
import { assert } from "../Utils";
import {
  getPaneIdFromPaneInput,
  isEmptyEvalExpression,
  parseNpmPackage,
} from "./SandboxUtils";

export const PANE_MARKER = (paneId: string) => `p${paneId}`;
export const PANE_INPUT_MARKER = (paneInputId: string) => `i${paneInputId}`;

export function getTypeModuleNameForImport(importModule: ImportModule): string {
  if (importModule[0] === "npm") {
    let importModuleName = importModule[1];
    if (importModuleName.startsWith("@")) {
      importModuleName = importModuleName.substring(1).replace("/", "__");
    }
    const firstSlashIndex = importModuleName.indexOf("/");
    if (firstSlashIndex !== -1) {
      return importModuleName.substring(0, firstSlashIndex);
    }
    return importModuleName;
  } else if (importModule[0] === "url") {
    return importModule[1].replace(/[:/.+]/g, "_");
  }
  return "unknown-script";
}

export function createEvalPaneFunction(
  p: Extract<Pane, { type: PaneType.Evaluate }>,
  options?: { allowUndefinedSource?: boolean; overrideExpression?: string }
) {
  const { allowUndefinedSource = false, overrideExpression } = options ?? {};
  const expression = overrideExpression ?? p.expression;
  return `async (${
    p.inputs.length > 0
      ? `inputs${p.inputs
          .map((paneInput) =>
            paneInput.name !== undefined &&
            (allowUndefinedSource || paneInput.source !== undefined)
              ? `, /*${PANE_INPUT_MARKER(paneInput.id)}*/${paneInput.name}`
              : undefined
          )
          .filter((v) => v !== undefined)}`
      : ""
  }) => {${
    p.expressionType === EvaluateExpressionType.Text
      ? `return ${JSON.stringify(expression)}`
      : p.expressionType === EvaluateExpressionType.FunctionBody
      ? `/*${PANE_MARKER(p.id)}*/${expression}`
      : `return ${!isEmptyEvalExpression(expression) ? "(" : ""}/*${PANE_MARKER(
          p.id
        )}*/${expression}${!isEmptyEvalExpression(expression) ? ")" : ""}`
  }}`;
}

export function convertPanesToTypeScriptCode(panes: Pane[]): string {
  const processedPaneIds = new Set<string>();
  const mainBlock: string[] = [];

  panes.forEach((p) => {
    if (p.type !== PaneType.Import) {
      return;
    }
    processedPaneIds.add(p.id);
    if (p.module === undefined) {
      return;
    }
    if (p.module[0] === "npm") {
      const npmPackage = parseNpmPackage(p.module[1]);
      mainBlock.push(
        `const ${CODE_ANALYSIS_VALUE_PREFIX}${
          p.id
        } = await import('${getTypeModuleNameForImport(p.module)}${
          npmPackage.export !== undefined ? `/${npmPackage.export}` : ""
        }');`
      );
    } else if (p.module[0] === "url") {
      mainBlock.push(
        `const ${CODE_ANALYSIS_VALUE_PREFIX}${
          p.id
        } = await import('${getTypeModuleNameForImport(p.module)}');`
      );
    }
    // TODO: handle p.module[0] === 'script'
    if (p.name !== undefined) {
      mainBlock.push(`const ${p.name} = ${CODE_ANALYSIS_VALUE_PREFIX}${p.id};`);
    }
  });

  panes.forEach((p) => {
    if (p.type === PaneType.EnvironmentVariable) {
      processedPaneIds.add(p.id);
      mainBlock.push(`const ${CODE_ANALYSIS_VALUE_PREFIX}${p.id} = "";`);
    }
    if (p.type === PaneType.Library) {
      processedPaneIds.add(p.id);
      mainBlock.push(`const ${CODE_ANALYSIS_VALUE_PREFIX}${p.id} = $natto;`);
    }
    if (p.type === PaneType.LLM) {
      processedPaneIds.add(p.id);
      mainBlock.push(`const ${CODE_ANALYSIS_VALUE_PREFIX}${p.id} = {text:""};`);
    }
  });

  panes.forEach((p) => {
    if (p.type !== PaneType.EvaluateGlobal) {
      return;
    }
    processedPaneIds.add(p.id);
    mainBlock.push(`const ${p.name} = await ${p.expression};`);
  });

  // then non-eval panes
  panes.forEach((p) => {
    if (p.type === PaneType.InputText) {
      mainBlock.push(
        `const ${CODE_ANALYSIS_VALUE_PREFIX}${p.id} = ${JSON.stringify(
          p.text
        )};`
      );
      processedPaneIds.add(p.id);
    } else if (p.type === PaneType.State) {
      mainBlock.push(
        `const ${CODE_ANALYSIS_VALUE_PREFIX}${p.id} = (() => {return ${
          !isEmptyEvalExpression(p.initialExpression) ? "(" : ""
        }/*${PANE_MARKER(p.id)}*/${p.initialExpression}${
          !isEmptyEvalExpression(p.initialExpression) ? ")" : ""
        }})();
function ${CODE_ANALYSIS_VALUE_PREFIX}${
          p.id
        }_1<T>(updater: T | ((value: T) => T)) {}`
      );
      processedPaneIds.add(p.id);
    }
  });

  function getValueName(inputPaneId: string, outputIndex: number) {
    if (outputIndex === 0) {
      return `${CODE_ANALYSIS_VALUE_PREFIX}${inputPaneId}`;
    }
    return `${CODE_ANALYSIS_VALUE_PREFIX}${inputPaneId}_${outputIndex}`;
  }

  function processEvalPanes(panes: Pane[]) {
    panes.forEach((p) => {
      processedPaneIds.add(p.id);
      assert(p.type === PaneType.Evaluate);
      mainBlock.push(
        `const ${CODE_ANALYSIS_VALUE_PREFIX}${
          p.id
        } = await (${createEvalPaneFunction(p)})(${
          p.inputs.length > 0
            ? `[${p.inputs
                .map((paneInput) =>
                  paneInput.source !== undefined
                    ? getValueName(paneInput.source[0], paneInput.source[1])
                    : "undefined"
                )
                .join(",")}] as const${p.inputs
                .map((paneInput) =>
                  paneInput.name !== undefined && paneInput.source !== undefined
                    ? `, ${getValueName(
                        paneInput.source[0],
                        paneInput.source[1]
                      )}`
                    : undefined
                )
                .filter((v) => v !== undefined)}`
            : ""
        });`
      );
    });
  }

  while (true) {
    const panesReadyToGo = panes.filter(
      (p) =>
        p.type === PaneType.Evaluate &&
        !processedPaneIds.has(p.id) &&
        !p.inputs.some((paneInput) => {
          const paneId = getPaneIdFromPaneInput(paneInput);
          return paneId === undefined || !processedPaneIds.has(paneId);
        })
    );
    if (panesReadyToGo.length === 0) {
      break;
    }
    processEvalPanes(panesReadyToGo);
  }

  return `async function ${CODE_ANALYSIS_VALUE_PREFIX}main() {
${mainBlock.map((l) => `  ${l}\n`).join("")}}\n`;
}
